import type { OpLogEntry } from "@livcg/schema";
import { AlertTriangle, MousePointerClick, Plug, Tv } from "lucide-react";
import { useState } from "react";
import { type OpLogFilter, useOpLog } from "../shared/oplog.js";
import { Button, cn, SegmentedControl } from "../ui/index.js";
import { Drawer } from "./control-room/Drawer.js";

const ACTION_LABEL: Record<string, string> = {
  "overlay.show": "Overlay efirga",
  "overlay.hide": "Overlay yashirish",
  "overlay.on": "Efirga chiqdi",
  "overlay.off": "Efirdan tushdi",
  "preview.set": "Preview tayyorlandi",
  "preview.clear": "Preview tozalandi",
  "var.show": "VAR ko'rsatish",
  "var.decide": "VAR qarori",
  "var.hide": "VAR yashirish",
  "var.on": "VAR efirda",
  "var.off": "VAR tushdi",
  "lineup.show": "Tarkib efirga",
  "lineup.hide": "Tarkib yashirish",
  "lineup.on": "Tarkib efirda",
  "lineup.off": "Tarkib tushdi",
  "extraTime.show": "Qo'shilgan vaqt",
  "extraTime.on": "Qo'shilgan vaqt efirda",
  "extraTime.off": "Qo'shilgan vaqt tushdi",
  "score.set": "Hisob o'rnatish",
  "score.adjust": "Hisob ±",
  "clock.toggle": "Soat start/stop",
  "clock.set": "Soat o'rnatish",
  "clock.adjust": "Soat ±",
  "clock.setPhase": "Bosqich",
  "clock.setAdditional": "Qo'shilgan vaqt",
  "event.add": "Hodisa qo'shish",
  "event.delete": "Hodisa o'chirish",
  "project.select": "Loyiha tanlash",
  "graphics.connect": "Grafika sahifasi ulandi",
  "graphics.disconnect": "Grafika sahifasi UZILDI",
  "panel.reconnect": "Panel aloqasi uzilib qoldi",
  "server.start": "Server qayta ishga tushdi",
  server: "Server xatosi",
  browser: "Brauzer xatosi",
};

/** Operator commands share action names with graphics entries (overlay.off), so they
 *  get their own labels. */
const COMMAND_LABEL: Record<string, string> = {
  "overlay.play": "Banner efirda",
  "overlay.stop": "Banner erta yopildi",
  "overlay.off": "Efirdan olish",
};

const actionLabel = (e: OpLogEntry): string | undefined =>
  (e.kind === "command" ? COMMAND_LABEL[e.action] : undefined) ?? ACTION_LABEL[e.action];

type Verdict = { label: string; className: string; icon: typeof Tv };

/** Shows at a glance "whose" the entry is. */
function verdict(e: OpLogEntry): Verdict {
  if (e.kind === "graphics") return { label: "Efir", className: "text-success", icon: Tv };
  if (e.kind === "command" && e.level === "info")
    return { label: "Operator", className: "text-accent-strong", icon: MousePointerClick };
  if (e.kind === "system" && e.level === "info")
    return { label: "Tizim", className: "text-ink-muted", icon: Plug };
  return { label: "Texnik", className: "text-danger-strong", icon: AlertTriangle };
}

function time(ts: number): string {
  return new Date(ts).toLocaleTimeString("uz-UZ", { hour12: false });
}

const KIND_OPTIONS: { value: OpLogFilter["kind"]; label: string }[] = [
  { value: "all", label: "Hammasi" },
  { value: "command", label: "Operator" },
  { value: "graphics", label: "Efir" },
  { value: "problems", label: "Xatolar" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  slug: string | null;
};

export function OpLogDrawer({ open, onClose, slug }: Props) {
  return (
    <Drawer open={open} onClose={onClose} title="Operator log" width="max-w-3xl">
      <OpLogBody slug={slug} />
    </Drawer>
  );
}

function OpLogBody({ slug }: { slug: string | null }) {
  const [kind, setKind] = useState<OpLogFilter["kind"]>("all");
  const [onlyProject, setOnlyProject] = useState(true);
  const filter: OpLogFilter = { slug: onlyProject ? slug : null, kind };
  const log = useOpLog(filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl options={KIND_OPTIONS} value={kind} onChange={setKind} />
        {slug && (
          <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted">
            <input
              type="checkbox"
              checked={onlyProject}
              onChange={(e) => setOnlyProject(e.target.checked)}
            />
            Faqat /{slug}
          </label>
        )}
      </div>

      <p className="text-[11.5px] leading-relaxed text-ink-muted">
        <b className="text-accent-strong">Operator</b> — tugma bosildi va server qabul qildi.{" "}
        <b className="text-success">Efir</b> — grafika haqiqatda chiqdi/tushdi.{" "}
        <b className="text-danger-strong">Texnik</b> — server rad etdi, javob bermadi, aloqa yoki
        grafika sahifasi uzildi. Tugma bosilgan-u "Efir" yozuvi yo'q bo'lsa — grafika tomonini
        tekshiring.
      </p>

      {log.error ? (
        <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-[13px] text-danger-strong">
          Log yuklanmadi: {log.error}
        </div>
      ) : log.loading && log.entries.length === 0 ? (
        <div className="py-10 text-center text-ink-muted">Yuklanmoqda…</div>
      ) : log.entries.length === 0 ? (
        <div className="py-10 text-center text-ink-muted">Yozuv yo'q</div>
      ) : (
        <ol className="divide-y divide-divider rounded-md border border-border">
          {log.entries.map((e) => (
            <OpLogRow key={e.id} entry={e} showSlug={!filter.slug} />
          ))}
        </ol>
      )}

      {log.hasMore && (
        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={log.loadMore}>
            Yana yuklash
          </Button>
        </div>
      )}
    </div>
  );
}

function OpLogRow({ entry: e, showSlug }: { entry: OpLogEntry; showSlug: boolean }) {
  const v = verdict(e);
  const Icon = v.icon;
  const failed = e.level !== "info";
  return (
    <li
      className={cn(
        "grid grid-cols-[64px_84px_1fr] items-start gap-2 px-3 py-2 text-[12.5px]",
        failed && "bg-danger/5",
      )}
    >
      <span className="font-mono tabular-nums text-ink-muted">{time(e.ts)}</span>
      <span className={cn("inline-flex items-center gap-1 font-medium", v.className)}>
        <Icon className="h-3.5 w-3.5 shrink-0" />
        {v.label}
      </span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium text-ink">{actionLabel(e) ?? e.action}</span>
          {actionLabel(e) && (
            <span className="font-mono text-[11px] text-ink-faint">{e.action}</span>
          )}
          {e.code && (
            <span className="rounded bg-danger/15 px-1.5 font-mono text-[11px] text-danger-strong">
              {e.code}
            </span>
          )}
          {e.username && <span className="text-[11.5px] text-ink-muted">@{e.username}</span>}
          {showSlug && e.slug && (
            <span className="font-mono text-[11px] text-ink-faint">/{e.slug}</span>
          )}
          {e.durationMs !== null && e.durationMs > 1000 && (
            <span className="text-[11px] text-warning">{(e.durationMs / 1000).toFixed(1)} s</span>
          )}
        </span>
        {e.detail && (
          <span className="mt-0.5 block break-all font-mono text-[11px] text-ink-muted">
            {e.detail}
          </span>
        )}
      </span>
    </li>
  );
}
